import type React from 'react'
import { createContext, type InputHTMLAttributes, type ReactNode, useContext, useState } from 'react'
import { cn, cva } from '../lib/cva'
import { Text } from './Text'

type RadioContextType = {
	value: string
	onChange: (value: string) => void
	name: string
	disabled?: boolean
	size?: 'small' | 'medium'
	variant?: RadioVariant
}

const RadioContext = createContext<RadioContextType | undefined>(undefined)

type RadioVariant = 'primary' | 'error'

interface RadioStyleProps {
	variant?: RadioVariant
	disabled?: boolean
	checked?: boolean
	size?: 'small' | 'medium'
}

interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange'>, RadioStyleProps {
	label?: string
	isRequired?: boolean
	value: string
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
}

const styles = {
	wrapper: cva('flex items-center gap-1.5 relative cursor-pointer transition-colors', {
		variants: {
			size: {
				medium: 'text-base',
				small: 'text-sm',
			},
			disabled: {
				true: 'cursor-not-allowed text-neutral-400',
				false: '',
			},
		},
		defaultVariants: {
			size: 'medium',
			disabled: false,
		},
	}),
	input: cva(
		'appearance-none w-5 h-5 rounded-full border border-neutral-200 bg-neutral-50 hover:bg-neutral-200 transition-colors cursor-pointer',
		{
			variants: {
				variant: {
					primary: '',
					error: '',
				},
				checked: {
					true: '',
					false: '',
				},
				disabled: {
					true: 'bg-neutral-200 border-neutral-200 hover:border-neutral-200 cursor-not-allowed',
					false: '',
				},
			},
			compoundVariants: [
				{
					checked: true,
					variant: 'primary',
					disabled: false,
					className: 'bg-primary-600 border-primary-600 hover:border-primary-600 hover:bg-primary-600',
				},
				{
					checked: true,
					variant: 'error',
					disabled: false,
					className: 'bg-error-600 border-error-600 hover:border-error-600 hover:bg-error-600',
				},
				{
					checked: true,
					variant: 'primary',
					disabled: true,
					className: 'bg-primary-300 border-primary-300 hover:border-primary-300',
				},
				{
					checked: true,
					variant: 'error',
					disabled: true,
					className: 'bg-error-300 border-error-300 hover:border-error-300',
				},
			],
			defaultVariants: {
				variant: 'primary',
				checked: false,
				disabled: false,
			},
		},
	),
	label: cva('select-none', {
		variants: {
			disabled: {
				true: 'text-neutral-400',
				false: 'text-neutral-800',
			},
		},
		defaultVariants: {
			disabled: false,
		},
	}),
}

export const Radio: React.FC<RadioProps> = ({
	className,
	size = 'medium',
	variant = 'primary',
	disabled = false,
	label,
	value,
	checked: controlledChecked,
	defaultChecked,
	onChange,
	isRequired = false,
	...props
}) => {
	const context = useContext(RadioContext)

	const [internalChecked, setInternalChecked] = useState<boolean>(controlledChecked ?? defaultChecked ?? false)

	const isChecked = context ? context.value === value : (controlledChecked ?? internalChecked)

	const effectiveSize = context?.size || size
	const effectiveVariant = context?.variant || variant
	const effectiveDisabled = context?.disabled || disabled

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (context) {
			context.onChange(value)
		} else {
			setInternalChecked(e.target.checked)
			onChange?.(e)
		}
	}

	const styleProps: RadioStyleProps = {
		variant: effectiveVariant,
		disabled: effectiveDisabled,
		checked: isChecked,
		size: effectiveSize,
	}

	return (
		<label className={cn(styles.wrapper({ size: effectiveSize, disabled: effectiveDisabled }), className)}>
			<input
				type='radio'
				className={styles.input(styleProps)}
				disabled={effectiveDisabled}
				checked={isChecked}
				onChange={handleChange}
				required={isRequired}
				name={context?.name}
				value={value}
				{...props}
			/>
			{isChecked && <div className='absolute rounded-full bg-neutral-0 left-[6.1px] w-2 h-2' />}
			{label && (
				<Text variant='none' className={styles.label({ disabled: effectiveDisabled })}>
					{`${label}${isRequired ? ' *' : ''}`}
				</Text>
			)}
		</label>
	)
}

interface RadioGroupProps {
	children: ReactNode
	name: string
	value?: string
	defaultValue?: string
	onChange?: (value: string) => void
	disabled?: boolean
	size?: 'small' | 'medium'
	variant?: RadioVariant
	className?: string
}

export const RadioGroup: React.FC<RadioGroupProps> = ({
	children,
	name,
	value: controlledValue,
	defaultValue,
	onChange,
	disabled = false,
	size = 'medium',
	variant = 'primary',
	className,
}) => {
	const [internalValue, setInternalValue] = useState<string>(defaultValue || '')

	const handleChange = (value: string) => {
		setInternalValue(value)
		onChange?.(value)
	}

	const currentValue = controlledValue ?? internalValue

	return (
		<RadioContext.Provider
			value={{
				name,
				value: currentValue,
				onChange: handleChange,
				disabled,
				size,
				variant,
			}}>
			<div className={cn('flex flex-col gap-2', className)}>{children}</div>
		</RadioContext.Provider>
	)
}
